import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Hexagon, Settings, ArrowLeft, Upload,
  LayoutDashboard, LineChart, ListChecks, Search,
  AlertTriangle, CheckSquare, DollarSign, Users, KeyRound, Loader2, Share2, Eye,
  Briefcase, ChevronUp, ChevronDown, Compass,
  ShieldCheck, Layers3, Database, Cable, TestTube2, GraduationCap,
  Radio, TrendingUp, Library, Zap, Sparkles, FileStack, HeartPulse,
} from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import { ProjectProvider, useProject } from "../state/ProjectContext";
import { Pill, Modal, Field } from "./ui-prim";
import { api } from "../lib/api";
import { statusClass, toast } from "../lib/utils";
import { getApiKey, setApiKey } from "../lib/ai";
import { accentVars, SECTION_ACCENTS } from "../lib/accents";
import { exportPDF, exportPPT, exportWord } from "../lib/export";
import { ExportMenu } from "./ExportMenu";
import { CheckInOut, CheckoutBanner } from "./CheckInOut";

import Dashboard from "./tabs/Dashboard";
import Tracker from "./tabs/Tracker";
import Requirements from "./tabs/Requirements";
import GapFit from "./tabs/GapFit";
import RAID from "./tabs/RAID";
import Actions from "./tabs/Actions";
import Budget from "./tabs/Budget";
import Stakeholders from "./tabs/Stakeholders";
import S4Hana from "./tabs/S4Hana";
import Strategy from "./tabs/Strategy";
import Governance from "./tabs/Governance";
import FitToStandard from "./tabs/FitToStandard";
import DataMigration from "./tabs/DataMigration";
import Integration from "./tabs/Integration";
import Testing from "./tabs/Testing";
import OCM from "./tabs/OCM";
import Cutover from "./tabs/Cutover";
import ValueRealization from "./tabs/ValueRealization";
import IPLibrary from "./tabs/IPLibrary";
import Integrations from "./tabs/Integrations";
import AILayer from "./tabs/AILayer";
import Documents from "./tabs/Documents";
import Diagnostics from "./tabs/Diagnostics";
import { useCSuiteAuth } from "../state/CSuiteAuthContext";

// Tabs available on the Starter plan; everything else is Pro-only.
const STARTER_TABS = new Set(["dashboard", "diagnostics", "tracker", "requirements", "documents", "gap", "raid", "actions", "budget", "stakeholders"]);

const TABS = [
  { id: "dashboard",       label: "Dashboard",    icon: LayoutDashboard, Comp: Dashboard },
  { id: "diagnostics",     label: "Diagnostics",  icon: HeartPulse,      Comp: Diagnostics },
  { id: "tracker",         label: "Tracker",      icon: LineChart,       Comp: Tracker },
  { id: "requirements",    label: "Requirements", icon: ListChecks,      Comp: Requirements },
  { id: "documents",       label: "Documents",    icon: FileStack,       Comp: Documents },
  { id: "governance",      label: "Governance",   icon: ShieldCheck,     Comp: Governance },
  { id: "fitToStandard",   label: "Fit-to-Standard", icon: Layers3,      Comp: FitToStandard },
  { id: "gap",             label: "Gap / Fit",    icon: Search,          Comp: GapFit },
  { id: "raid",            label: "RAID Log",     icon: AlertTriangle,   Comp: RAID },
  { id: "actions",         label: "Actions",      icon: CheckSquare,     Comp: Actions },
  { id: "dataMigration",   label: "Data Migration", icon: Database,      Comp: DataMigration },
  { id: "integration",     label: "Integration",  icon: Cable,           Comp: Integration },
  { id: "testing",         label: "Testing",      icon: TestTube2,       Comp: Testing },
  { id: "ocm",             label: "OCM & Training", icon: GraduationCap, Comp: OCM },
  { id: "cutover",         label: "Cutover",      icon: Radio,           Comp: Cutover },
  { id: "budget",          label: "Budget",       icon: DollarSign,      Comp: Budget },
  { id: "stakeholders",    label: "Stakeholders", icon: Users,           Comp: Stakeholders },
  { id: "valueRealization",label: "Value",        icon: TrendingUp,      Comp: ValueRealization },
  { id: "s4hana",          label: "S/4HANA",      icon: Briefcase,       Comp: S4Hana },
  { id: "strategy",        label: "Strategy",     icon: Compass,         Comp: Strategy },
  { id: "ipLibrary",       label: "IP Library",   icon: Library,         Comp: IPLibrary },
  { id: "integrations",    label: "Integrations", icon: Zap,             Comp: Integrations },
  { id: "ai",              label: "AI Layer",     icon: Sparkles,        Comp: AILayer },
];

export default function Workspace({ readOnly = false }) {
  const { id } = useParams();
  const [params] = useSearchParams();
  const shareToken = params.get("t");
  return (
    <ProjectProvider projectId={id} readOnly={readOnly} shareToken={shareToken}>
      <WorkspaceInner />
    </ProjectProvider>
  );
}

function WorkspaceInner() {
  const { doc, loading, loadError, saving, mutate, replaceDoc, readOnly } = useProject();
  const nav = useNavigate();
  const [urlParams] = useSearchParams();
  const { user } = useCSuiteAuth();
  const plan = user?.subscription?.plan;
  const isAdmin = user?.role === "ADMIN";
  // Filter TABS based on subscription. ADMIN and Pro see everything;
  // Starter sees the STARTER_TABS subset. Read-only shares also see all.
  const visibleTabs = React.useMemo(() => {
    if (readOnly || isAdmin || plan === "pro") return TABS;
    if (plan === "starter") return TABS.filter(t => STARTER_TABS.has(t.id));
    return TABS; // no subscription — show everything but they'll be gated at the API level
  }, [plan, isAdmin, readOnly]);
  // Initial tab from ?tab= URL query so deep-links from the C-Suite
  // "Distress Signals" card land on the Diagnostics tab.
  const initialTab = React.useMemo(() => {
    const q = urlParams.get("tab");
    if (q && TABS.some(t => t.id === q)) return q;
    return "dashboard";
  }, [urlParams]);
  const [tab, setTab] = useState(initialTab);
  const [tabsOpen, setTabsOpen] = useState(() => {
    try { return localStorage.getItem("pmv5_tabs_collapsed") !== "1"; } catch { return true; }
  });
  const toggleTabs = () => {
    setTabsOpen(v => {
      const next = !v;
      try { localStorage.setItem("pmv5_tabs_collapsed", next ? "0" : "1"); } catch { /* ignore */ }
      return next;
    });
  };
  const [settings, setSettings] = useState(false);
  const [apiKey, setKey] = useState(getApiKey());
  const fileRef = useRef(null);

  // sync key field when modal opens
  useEffect(() => { if (settings) setKey(getApiKey()); }, [settings]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-zinc-500 font-mono text-sm">
      <Loader2 size={20} className="animate-spin mr-2 inline" /> Loading project…
    </div>;
  }
  if (loadError) {
    return <div className="min-h-screen grid place-items-center px-6">
      <div className="max-w-md text-center">
        <div className="font-display text-2xl font-bold text-zinc-900 mb-2">Can't open this project</div>
        <div className="text-sm text-zinc-600">{loadError}</div>
        <button className="btn btn-primary mt-4" onClick={() => nav("/projects")}>Back to projects</button>
      </div>
    </div>;
  }
  if (!doc) {
    return <div className="min-h-screen grid place-items-center text-zinc-500 font-mono">
      Project not found · <button className="underline ml-2" onClick={() => nav("/")}>Back</button>
    </div>;
  }

  const ActiveComp = (visibleTabs.find(t => t.id === tab) || visibleTabs[0]).Comp;
  const meta = doc.meta || {};

  // Export JSON
  const onExport = () => {
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${meta.code || "project"}-${new Date().toISOString().split("T")[0]}.json`;
    a.click();
    toast("Exported");
  };

  const onImport = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = async (ev) => {
      try {
        const d = JSON.parse(ev.target.result);
        const next = await api.import(doc.id, d);
        replaceDoc(next);
        toast("Imported");
      } catch (err) { toast("Import failed: " + err.message); }
    };
    r.readAsText(f);
    e.target.value = "";
  };

  return (
    <div className="min-h-screen flex flex-col grid-bg">
      {/* HEADER */}
      <header className="border-b border-[var(--border)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-5 lg:px-8 h-16 flex items-center gap-4">
          <button className="btn btn-ghost btn-sm" onClick={() => nav("/projects")} title="Back to projects" data-testid="btn-back">
            <ArrowLeft size={14}/>
          </button>
          <div className="w-9 h-9 brand-chip flex items-center justify-center shadow-sm">
            <Hexagon size={18} strokeWidth={2.2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold tracking-tight text-zinc-900 truncate" data-testid="header-project-name">{meta.name}</div>
            <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.14em] truncate">
              {meta.code} · PM: {meta.pm || "—"} · {meta.start} → {meta.end}
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <Pill tone={statusClass(meta.phase).replace("pill-", "")}>{meta.phase}</Pill>
            <Pill tone={statusClass(meta.health).replace("pill-", "")}>{meta.health}</Pill>
            {!readOnly ? (
              <select
                className="fld py-1 px-2 text-xs font-mono uppercase tracking-[0.14em]"
                style={{ height: 28, width: 130 }}
                value={meta.methodology || "Waterfall"}
                onChange={e => mutate(d => ({ ...d, meta: { ...d.meta, methodology: e.target.value } }))}
                data-testid="header-methodology"
                title="Delivery methodology · shapes the Gantt view and RACI defaults"
              >
                {["Waterfall", "Agile", "Hybrid"].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <Pill tone="default">{meta.methodology || "Waterfall"}</Pill>
            )}
          </div>
          <div className="font-mono text-[10px] text-zinc-500 hidden sm:block min-w-[60px] text-right">
            {readOnly ? "Read-only" : (saving ? "Saving…" : "Saved")}
          </div>
          {!readOnly && (
            <button className="btn btn-secondary btn-sm" onClick={async () => {
              try {
                const { token } = await api.share(doc.id, 7);
                const url = `${window.location.origin}/share/${doc.id}?t=${token}`;
                await navigator.clipboard?.writeText(url);
                toast("Signed share link copied (expires in 7 days)");
              } catch (e) {
                toast("Failed to issue link: " + (e?.response?.data?.detail || e.message));
              }
            }} data-testid="btn-share" title="Issue a signed 7-day share link">
              <Share2 size={13}/> <span className="hidden sm:inline">Share</span>
            </button>
          )}
          {!readOnly && <CheckInOut />}
          {!readOnly && (
            <button className="btn btn-secondary btn-sm" onClick={() => setSettings(true)} data-testid="btn-settings">
              <Settings size={13}/> <span className="hidden sm:inline">Settings</span>
            </button>
          )}
          <ThemeToggle />
        </div>

        {/* TABS */}
        {tabsOpen ? (
          <div className="border-t border-[var(--border)] overflow-x-auto" data-testid="tabs-nav-expanded">
            <div className="max-w-[1600px] mx-auto px-5 lg:px-8 flex gap-1 items-center">
              {visibleTabs.map(t => {
                const Icon = t.icon;
                const on = tab === t.id;
                const acc = SECTION_ACCENTS[t.id];
                return (
                  <button key={t.id}
                          onClick={() => setTab(t.id)}
                          data-testid={`tab-${t.id}`}
                          style={on ? { "--accent-a": acc.a, "--accent-b": acc.b } : {}}
                          className={`relative flex items-center gap-2 px-4 py-3 text-xs font-mono uppercase tracking-[0.1em] transition-colors whitespace-nowrap
                                      ${on ? "tab-active text-zinc-900" : "text-zinc-500 hover:text-zinc-700"}`}>
                    <Icon size={13} strokeWidth={1.8} style={on ? { color: acc.a } : {}} />
                    {t.label}
                  </button>
                );
              })}
              <button
                onClick={toggleTabs}
                className="ml-auto px-3 py-3 text-zinc-500 hover:text-zinc-800 transition-colors"
                title="Collapse tabs"
                data-testid="btn-collapse-tabs"
              >
                <ChevronUp size={14}/>
              </button>
            </div>
          </div>
        ) : (
          <div className="border-t border-[var(--border)]" data-testid="tabs-nav-collapsed">
            <div className="max-w-[1600px] mx-auto px-5 lg:px-8 py-2 flex items-center gap-2">
              {(() => {
                const current = visibleTabs.find(t => t.id === tab) || visibleTabs[0];
                const Icon = current.icon;
                const acc = SECTION_ACCENTS[current.id];
                return (
                  <>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[var(--surface)] border border-[var(--border)]"
                         style={{ "--accent-a": acc.a, "--accent-b": acc.b }}>
                      <Icon size={13} strokeWidth={1.8} style={{ color: acc.a }}/>
                      <span className="text-xs font-mono uppercase tracking-[0.1em] text-zinc-800">{current.label}</span>
                    </div>
                    <select
                      value={tab}
                      onChange={(e) => setTab(e.target.value)}
                      className="text-xs font-mono uppercase tracking-[0.1em] bg-transparent border border-[var(--border)] rounded-sm px-2 py-1.5 text-zinc-600 hover:text-zinc-900 cursor-pointer"
                      data-testid="tabs-collapsed-select"
                      title="Switch tab"
                    >
                      {visibleTabs.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </select>
                    <button
                      onClick={toggleTabs}
                      className="ml-auto px-3 py-1.5 text-zinc-500 hover:text-zinc-800 transition-colors flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.14em]"
                      title="Expand tabs"
                      data-testid="btn-expand-tabs"
                    >
                      <ChevronDown size={13}/> Show all tabs
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </header>

      {/* CONTENT */}
      <main className="flex-1 max-w-[1600px] w-full mx-auto px-5 lg:px-8 py-8" style={accentVars(tab)} data-section={tab}>
        {readOnly && (
          <div className="mb-6 flex items-center gap-3 bg-white/60 border border-[var(--border)] rounded-sm px-4 py-3" data-testid="readonly-banner">
            <Eye size={14} className="text-zinc-500 shrink-0"/>
            <div className="text-xs text-zinc-700">
              <span className="font-mono uppercase tracking-[0.14em] text-zinc-500">Read-only share</span>
              <span className="mx-2 text-zinc-300">·</span>
              Viewing a snapshot of this project. Changes are disabled.
            </div>
            <button className="btn btn-secondary btn-sm ml-auto" onClick={() => {
              navigator.clipboard?.writeText(window.location.href);
              toast("Link copied");
            }}>
              <Share2 size={12}/> Copy link
            </button>
          </div>
        )}
        <div className="anim-in">
          <CheckoutBanner />
          <ActiveComp />
        </div>
      </main>

      {/* BOTTOM STRIP — API key + Export/Import */}
      {!readOnly && (
      <footer className="border-t border-[var(--border)] bg-white/85 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-5 lg:px-8 py-3 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-zinc-500 font-mono text-[10px] uppercase tracking-[0.14em]">
            <KeyRound size={12}/> Anthropic key
          </div>
          <input type="password"
                 className="fld flex-1 min-w-[200px] max-w-[420px]"
                 placeholder="sk-ant-… enables AI features"
                 defaultValue={getApiKey()}
                 onChange={(e) => setApiKey(e.target.value)}
                 data-testid="input-api-key" />
          <div className="ml-auto flex gap-2">
            <ExportMenu
              onPDF={async () => { try { toast("Capturing visuals…"); await exportPDF(doc); toast("PDF generated"); } catch (e) { toast("PDF failed: " + e.message); } }}
              onWord={async () => { try { toast("Capturing visuals…"); await exportWord(doc); toast("Word generated"); } catch (e) { toast("Word failed: " + e.message); } }}
              onPPT={async () => { try { toast("Capturing visuals…"); await exportPPT(doc); toast("PowerPoint generated"); } catch (e) { toast("PPT failed: " + e.message); } }}
              onJSON={onExport}
            />
            <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()} data-testid="btn-import">
              <Upload size={12}/> Import
            </button>
            <input ref={fileRef} type="file" accept=".json" hidden onChange={onImport} />
          </div>
        </div>
      </footer>
      )}

      {/* SETTINGS MODAL */}
      <Modal open={settings} onClose={() => setSettings(false)} title="Project settings"
             footer={<button className="btn btn-primary" onClick={() => setSettings(false)} data-testid="btn-close-settings">Done</button>}>
        <div className="font-mono text-[10px] text-zinc-500 mb-3">All changes save automatically as you type.</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Project name"><input id="set-name" className="fld" defaultValue={meta.name} data-testid="input-set-name"
                  onBlur={e => mutate(d => ({ ...d, meta: { ...d.meta, name: e.target.value } }))}/></Field>
          <Field label="Code"><input id="set-code" className="fld" defaultValue={meta.code}
                  onBlur={e => mutate(d => ({ ...d, meta: { ...d.meta, code: e.target.value } }))}/></Field>
          <Field label="Phase">
            <select id="set-phase" className="fld" defaultValue={meta.phase}
                    onChange={e => mutate(d => ({ ...d, meta: { ...d.meta, phase: e.target.value } }))}>
              {["Planning","Design","Execution","UAT","Closure"].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Health">
            <select id="set-health" className="fld" defaultValue={meta.health}
                    onChange={e => mutate(d => ({ ...d, meta: { ...d.meta, health: e.target.value } }))}>
              {["On Track","At Risk","Off Track","On Hold"].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Methodology">
            <select id="set-methodology" className="fld" defaultValue={meta.methodology || "Waterfall"}
                    data-testid="input-set-methodology"
                    onChange={e => mutate(d => ({ ...d, meta: { ...d.meta, methodology: e.target.value } }))}>
              {["Waterfall","Agile","Hybrid"].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Sponsor"><input id="set-sponsor" className="fld" defaultValue={meta.sponsor}
                  onBlur={e => mutate(d => ({ ...d, meta: { ...d.meta, sponsor: e.target.value } }))}/></Field>
          <Field label="PM"><input id="set-pm" className="fld" defaultValue={meta.pm}
                  onBlur={e => mutate(d => ({ ...d, meta: { ...d.meta, pm: e.target.value } }))}/></Field>
          <Field label="Start"><input id="set-start" type="date" className="fld" defaultValue={meta.start}
                  onBlur={e => mutate(d => ({ ...d, meta: { ...d.meta, start: e.target.value } }))}/></Field>
          <Field label="End"><input id="set-end" type="date" className="fld" defaultValue={meta.end}
                  onBlur={e => mutate(d => ({ ...d, meta: { ...d.meta, end: e.target.value } }))}/></Field>
          <Field label="Budget (USD)">
            <input id="set-budget" type="number" className="fld" defaultValue={meta.budget}
                   onBlur={e => mutate(d => ({ ...d, meta: { ...d.meta, budget: Number(e.target.value) || 0 } }))}/>
          </Field>
          <Field label="ROI (e.g. 18% / 3yr)">
            <input id="set-roi" className="fld" defaultValue={meta.roi || ""} placeholder="e.g. 18% / 3yr" data-testid="input-set-roi"
                   onBlur={e => mutate(d => ({ ...d, meta: { ...d.meta, roi: e.target.value } }))}/>
          </Field>
        </div>
        <div className="border-t border-[var(--border)] pt-4">
          <Field label="Anthropic API key">
            <input type="password" className="fld" value={apiKey} onChange={e => { setKey(e.target.value); setApiKey(e.target.value); }} placeholder="sk-ant-…" data-testid="input-settings-apikey" />
          </Field>
          <div className="font-mono text-[10px] text-zinc-500 mt-1">Stored locally — sent only to api.anthropic.com.</div>
        </div>
      </Modal>
    </div>
  );
}
